/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
/**
 * VxAddToCart component.
 *
 * This component is used to render an add-to-cart button with various properties.
 *
 * @example
 * <vx-add-to-cart label="Add to cart"></vx-add-to-cart>
 */
// eslint-disable-next-line require-jsdoc
export class VxAddToCart {
    constructor() {
        this.defaultOption = { value: '1', text: '1' };
        /**
         * Adds the product to the cart.
         */
        this.addCartItem = () => {
            if (this.disabled)
                return;
            this.addToCartClicked.emit({
                quantity: this.quantity,
                orderNumber: this.orderNumber,
            });
            this.addToCartTrackingEnrich.emit({
                quantity: this.quantity,
                orderNumber: this.orderNumber,
                context: this.context,
                action: this.action,
            });
        };
        /**
         * Handler for quantity state change.
         *
         * @param {Event} event - The select change event.
         */
        this.handleQuantityChange = (event) => {
            this.quantity = Number(event.detail.value);
        };
        this.label = undefined;
        this.raised = true;
        this.disabled = undefined;
        this.fullwidth = true;
        this.displayQuantitySelect = true;
        this.quantity = 1;
        this.maxQuantity = 150;
        this.icon = undefined;
        this.buttonHeight = undefined;
        this.buttonWidth = undefined;
        this.loading = false;
        this.itemAdded = false;
        this.context = '';
        this.action = 'not_set';
        this.orderNumber = undefined;
        this.selectOptions = undefined;
        this.hasFocus = false;
    }
    /**
     * Listens for mouseenter, mouseleave, and touchstart events to toggle focus state.
     */
    toggleFocus(e) {
        this.hasFocus = ['mouseenter', 'touchstart', 'focus'].includes(e.type);
    }
    /**
     * Watch for changes in focus state to generate select options.
     */
    generateOptions() {
        if (!this.hasFocus || this.selectOptions)
            return;
        this.selectOptions = Array.from(Array(this.maxQuantity).keys()).map(i => ({
            value: String(i + 1),
            text: String(i + 1),
        }));
    }
    /**
     * Renders the VxAddToCart component.
     */
    render() {
        var _a, _b;
        return (h(Host, { key: 'a5e3a922316c2c380f1bd9c53ba0e2a8d326987c' }, h("div", { key: '4a72166478f7fbad1a1aa26fc88652bb8ae7f835', class: { 'is--loading': this.loading, 'btn-add-wrapper': true, 'has--focus': this.hasFocus } }, this.displayQuantitySelect && !this.disabled && (h("vx-select", { "aria-label": "Menge", "aria-disabled": this.disabled, disabled: this.disabled, options: (_a = this.selectOptions) !== null && _a !== void 0 ? _a : [this.defaultOption], onChanged: this.handleQuantityChange })), h("vx-button", { key: 'ce0a9b97662ae74ed98b3378f045ee338c26b2da', "aria-label": "Zum Warenkorb hinzuf\u00FCgen", height: this.buttonHeight, width: this.buttonWidth, success: this.itemAdded, disabled: this.disabled, raised: this.raised, fullwidth: !this.buttonWidth, class: "btn-add", icon: this.icon, onClick: this.addCartItem }, h("vx-circular-progress", { key: 'd41731a358f6d24f252b388bab8bf515377cc090', show: this.hasFocus && this.loading }), h("span", { key: 'eba027fc87522cc01a9b02e5ec9d284880ed7007', class: "vx-button-content" }, (_b = this.label) !== null && _b !== void 0 ? _b : (h("slot", null)))))));
    }
    static get is() { return "vx-add-to-cart"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-add-to-cart.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-add-to-cart.css"]
        };
    }
    static get properties() {
        return {
            "label": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The label of the button."
                },
                "attribute": "label",
                "reflect": false
            },
            "raised": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is raised."
                },
                "attribute": "raised",
                "reflect": false,
                "defaultValue": "true"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is disabled."
                },
                "attribute": "disabled",
                "reflect": false
            },
            "fullwidth": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is full container width."
                },
                "attribute": "fullwidth",
                "reflect": false,
                "defaultValue": "true"
            },
            "displayQuantitySelect": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the quantity select field is visible."
                },
                "attribute": "display-quantity-select",
                "reflect": false,
                "defaultValue": "true"
            },
            "quantity": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The quantity of the product to add to the cart."
                },
                "attribute": "quantity",
                "reflect": false,
                "defaultValue": "1"
            },
            "maxQuantity": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The maximum number of items to select as quantity in the quantity selector."
                },
                "attribute": "max-quantity",
                "reflect": false,
                "defaultValue": "150"
            },
            "icon": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The icon of the button."
                },
                "attribute": "icon",
                "reflect": false
            },
            "buttonHeight": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is quadratic."
                },
                "attribute": "button-height",
                "reflect": false
            },
            "buttonWidth": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "button-width",
                "reflect": false
            },
            "loading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Whether the button is loading."
                },
                "attribute": "loading",
                "reflect": false,
                "defaultValue": "false"
            },
            "itemAdded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Whether the item has been added to the cart."
                },
                "attribute": "item-added",
                "reflect": false,
                "defaultValue": "false"
            },
            "context": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The context (position) of the button used to track add to cart performance."
                },
                "attribute": "context",
                "reflect": false,
                "defaultValue": "''"
            },
            "action": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The action of the button used to track add to cart performance."
                },
                "attribute": "action",
                "reflect": false,
                "defaultValue": "'not_set'"
            },
            "orderNumber": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The order number (sku) of the product."
                },
                "attribute": "order-number",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "selectOptions": {},
            "hasFocus": {}
        };
    }
    static get events() {
        return [{
                "method": "addToCartClicked",
                "name": "addToCartClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the add to cart button is clicked."
                },
                "complexType": {
                    "original": "{ quantity: number, orderNumber?: string }",
                    "resolved": "{ quantity: number; orderNumber?: string; }",
                    "references": {}
                }
            }, {
                "method": "addToCartTrackingEnrich",
                "name": "addToCartTrackingEnrich",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the add to cart button is clicked."
                },
                "complexType": {
                    "original": "{ quantity: number, orderNumber?: string, context?: string, action?: string }",
                    "resolved": "{ quantity: number; orderNumber?: string; context?: string; action?: string; }",
                    "references": {}
                }
            }];
    }
    static get watchers() {
        return [{
                "propName": "hasFocus",
                "methodName": "generateOptions"
            }];
    }
    static get listeners() {
        return [{
                "name": "mouseenter",
                "method": "toggleFocus",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mouseleave",
                "method": "toggleFocus",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "touchstart",
                "method": "toggleFocus",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "focus",
                "method": "toggleFocus",
                "target": undefined,
                "capture": false,
                "passive": true
            }];
    }
}
//# sourceMappingURL=vx-add-to-cart.js.map
